import {isInstanceOf, insertAfter, insertBefore, narrowInstance} from "@cm/utils"
import {
    getOwnedNodes,
    ObjectPathPart,
    MeshCurve,
    Seam,
    initDimensionGuides,
    ImageRgbCurve,
    getAllTemplateNodes,
    isMaterialLikeNode,
    HdriLight,
} from "@cm/template-nodes"
import {
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    viewChild,
    ViewContainerRef,
    signal,
    ChangeDetectionStrategy,
} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog} from "@angular/material/dialog"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {Router} from "@angular/router"
import {TemplateState, TemplateType} from "@generated"
import {RenameDialogComponent} from "@app/common/components/dialogs/rename-dialog/rename-dialog.component"
import {FilesService} from "@app/common/services/files/files.service"
import {TemplateMenuSectionComponent} from "@app/template-editor/components/template-menu-section/template-menu-section.component"
import {TemplateMenuComponent} from "@app/template-editor/components/template-menu/template-menu.component"
import {DIALOG_DEFAULT_WIDTH} from "@app/template-editor/helpers/constants"
import {objectPathPartEquals, SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {TemplateNodeClipboardService} from "@app/template-editor/services/template-node-clipboard.service"
import {
    DraggableSource,
    DropPosition,
    getDropPositionFromPosition,
    TemplateDropTarget,
    TemplateNodeDragService,
} from "@app/template-editor/services/template-node-drag.service"
import {Matrix4} from "@cm/math"
import {
    ConfigGroup,
    ConfigVariant,
    DataObjectReference,
    DimensionGuideBox,
    DimensionGuides,
    getNodeOwner,
    getTemplateNodeClassLabel,
    getTemplateNodeLabel,
    getTemplateSwitchItemLabel,
    Group,
    ImageOperator,
    isImageLikeNode,
    isNamedNode,
    isTemplateLikeNode,
    MaterialReference,
    MeshDecal,
    TemplateNode,
    NodeOwnerLikeNode,
    TemplateNodes,
    Parameters,
    SceneNodes,
    StoredMesh,
    TemplateGraph,
    TemplateInstance,
    TemplateListNode,
    TemplateReference,
    fundamentalTypes,
    type AnyFundamentalType,
    MoveMaterial,
    MoveImage,
    ObjectNode,
    isNodeOwnerLikeNode,
    MeshNode,
    SwitchNode,
} from "@cm/template-nodes"
import {Mattress} from "@cm/mattress-nodes"
import {ListItemComponent} from "@common/components/item"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {TemplateMenuItemComponent} from "@template-editor/components/template-menu-item/template-menu-item.component"
import {
    CreateTemplateRevisionForTemplateTreeItemGQL,
    GetMaterialDetailsForTemplateTreeItemGQL,
    GetTemplateDetailsForTemplateTreeItemGQL,
} from "@template-editor/components/template-tree-item/template-tree-item.generated"
import {hasChildren} from "@template-editor/components/template-tree/template-tree-types"
import type {TemplateTreeNode} from "@template-editor/components/template-tree/template-tree-types"
import type {TemplateTreeComponent} from "@template-editor/components/template-tree/template-tree.component"
import {getNodeIconClass, getNodeIconSecondaryClass, isTemplateValueNode} from "@template-editor/helpers/template-icons"
import {firstValueFrom} from "rxjs"
import {tap} from "rxjs/operators"
import {v4 as uuid4} from "uuid"
import {TemplateNodeComponent} from "@app/template-editor/components/template-node/template-node.component"
import {TemplateTreeObjectTransformComponent} from "@app/template-editor/components/template-tree-object-transform/template-tree-object-transform.component"
import {TemplateAddDialogComponent, TemplateAddDialogComponentData} from "@app/template-editor/components/template-add-dialog/template-add-dialog.component"
import {OrganizationsService} from "@app/common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {isApple} from "@cm/browser-utils"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"
import {createLinkToEditorFromTemplatesForType} from "@app/common/helpers/routes"
import {TemplateTreeSceneNodeInfoTreeComponent} from "@app/template-editor/components/template-tree-scene-node-info-tree/template-tree-scene-node-info-tree.component"
import {NgTemplateOutlet} from "@angular/common"
import {TemplateTreeSceneNodeAggregationComponent} from "@app/template-editor/components/template-tree-scene-node-aggregation/template-tree-scene-node-aggregation.component"
import {BlenderConnectorService, type BlenderLightSource} from "@app/template-editor/services/blender-connector.service"
import {
    SendToBlenderDialogComponent,
    type SendToBlenderDialogData,
    type SendToBlenderDialogResult,
} from "@app/template-editor/components/send-to-blender-dialog/send-to-blender-dialog.component"

const countOf = (count: number, singular: string, plural: string) => (count === 0 ? "" : `${count} ${count === 1 ? singular : plural}`)

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-tree-item",
    templateUrl: "./template-tree-item.component.html",
    styleUrls: ["./template-tree-item.component.scss", "./../../helpers/template-icons.scss"],
    imports: [
        ListItemComponent,
        MatMenuModule,
        TemplateMenuComponent,
        TemplateMenuSectionComponent,
        MatTooltipModule,
        TemplateMenuItemComponent,
        TemplateTreeObjectTransformComponent,
        TemplateTreeSceneNodeInfoTreeComponent,
        NgTemplateOutlet,
        TemplateTreeSceneNodeAggregationComponent,
    ],
})
export class TemplateTreeItemComponent implements OnInit, OnDestroy {
    readonly sceneManagerService = inject(SceneManagerService)
    private readonly router = inject(Router)
    private readonly notifications = inject(NotificationsService)
    private readonly dialog = inject(MatDialog)
    private elementRef = inject<ElementRef<HTMLElement>>(ElementRef)
    private readonly cdr = inject(ChangeDetectorRef)
    readonly clipboardService = inject(TemplateNodeClipboardService)
    readonly drag = inject(TemplateNodeDragService)
    readonly files = inject(FilesService)
    protected readonly destroyRef = inject(DestroyRef)
    readonly templateGql = inject(GetTemplateDetailsForTemplateTreeItemGQL)
    readonly materialGql = inject(GetMaterialDetailsForTemplateTreeItemGQL)
    readonly organizations = inject(OrganizationsService)
    private readonly permission = inject(PermissionsService)
    private readonly $can = this.permission.$to
    readonly createTemplateRevision = inject(CreateTemplateRevisionForTemplateTreeItemGQL)
    private readonly notificationService = inject(NotificationsService)
    readonly blenderConnectorService = inject(BlenderConnectorService, {optional: true})
    TemplateInstance = TemplateInstance
    MeshDecal = MeshDecal
    MeshCurve = MeshCurve
    Mattress = Mattress
    DimensionGuides = DimensionGuides
    DimensionGuideBox = DimensionGuideBox
    Seam = Seam
    MoveMaterial = MoveMaterial
    ImageOperator = ImageOperator
    ImageRgbCurve = ImageRgbCurve
    MoveImage = MoveImage

    readonly $treeNode = input.required<TemplateTreeNode>({alias: "treeNode"})
    readonly $sceneNodeInfo = computed(() => {
        const sceneNodeInfo = this.$templateTree().$sceneNodeInfo()
        if (!sceneNodeInfo) return undefined

        return sceneNodeInfo.filter((sceneNode) => sceneNode.path.includes(this.$treeNode().node))
    })

    readonly $node = computed(() => this.$treeNode().node)
    readonly $isNodeValue = computed(() => isTemplateValueNode(this.$node()))
    readonly $isRootNode = computed(() => this.$node() === this.sceneManagerService.$templateGraph())
    readonly $parent = computed(() => this.$treeNode().parent)
    readonly $isHighlighted = computed(() => this.$templateTree().isHighlighted(this.$treeNode()) || this.sceneManagerService.isHighlightedNode(this.$node()))
    readonly $sceneNodeInfoExpandable = computed(() => {
        if (this.$nodeOwnerNode() !== undefined) return false
        const sceneNodeInfo = this.$sceneNodeInfo()
        if (!sceneNodeInfo) return false
        if (sceneNodeInfo.length === 0) return false
        if (sceneNodeInfo.length > 1) return true
        const firstNode = sceneNodeInfo[0]
        const {path} = firstNode
        if (path.length === 0) return true
        const lastPathItem = path[path.length - 1]
        return lastPathItem !== this.$node()
    })
    readonly $expandable = computed(() => hasChildren(this.$treeNode()) || this.$sceneNodeInfoExpandable())
    readonly $disabled = computed(() => !this.sceneManagerService.isNodeActive(this.$node()))
    readonly $selected = computed(() => this.sceneManagerService.$selectedRootNodeParts().some(({templateNode}) => templateNode === this.$node()))
    readonly $templateTree = input.required<TemplateTreeComponent>({alias: "templateTree"})
    readonly $nodeIconClass = computed(() => getNodeIconClass(this.$node().nodeClass))
    readonly $secondaryNodeIconClass = computed(() => getNodeIconSecondaryClass(this.$node().nodeClass))
    readonly $class = computed(() => getTemplateNodeClassLabel(this.$node()))
    readonly $label = computed(() => getTemplateNodeLabel(this.$node()))
    readonly $storedMeshNode = computed(() => narrowInstance(this.$node(), StoredMesh))
    readonly $dataObjectReferenceNode = computed(() => narrowInstance(this.$node(), DataObjectReference))
    readonly $objectNode = computed(() => narrowInstance(this.$node(), ObjectNode))
    readonly $imageLikeNode = computed(() => {
        const node = this.$node()
        if (isImageLikeNode(node)) return node
        else return undefined
    })
    readonly $materialLikeNode = computed(() => {
        const node = this.$node()
        if (isMaterialLikeNode(node)) return node
        else return undefined
    })
    readonly $templateReferenceNode = computed(() => {
        const node = this.$node()
        if (node instanceof TemplateReference) return node
        else if (node instanceof TemplateInstance && node.parameters.template instanceof TemplateReference) return node.parameters.template
        else return undefined
    })
    readonly $materialReferenceNode = computed(() => narrowInstance(this.$node(), MaterialReference))
    readonly $nodeOwnerNode = computed(() => {
        const node = this.$node()
        if (isNodeOwnerLikeNode(node)) return node
        else return undefined
    })
    readonly $templateLikeNode = computed(() => {
        const node = this.$node()
        if (isTemplateLikeNode(node)) return node
        else return undefined
    })
    readonly $templateGraphNode = computed(() => narrowInstance(this.$node(), TemplateGraph))
    readonly $templateInstanceNode = computed(() => narrowInstance(this.$node(), TemplateInstance))
    readonly $namedNode = computed(() => {
        const node = this.$node()
        if (isNamedNode(node)) return node
        else return undefined
    })
    readonly $groupNode = computed(() => narrowInstance(this.$node(), Group))
    readonly $meshNode = computed(() => narrowInstance(this.$node(), MeshNode))
    readonly $mattressNode = computed(() => narrowInstance(this.$node(), Mattress))
    readonly $meshCurveNode = computed(() => narrowInstance(this.$node(), MeshCurve))
    readonly $switchNode = computed(() => narrowInstance(this.$node(), SwitchNode))
    readonly $parentSwitchNode = computed(() => narrowInstance(this.$parent()?.node, SwitchNode))
    readonly $objectProcessingNodes = computed(() => [...this.getProcessingNodes(false)].filter(isInstanceOf(ObjectNode)))
    readonly $getSwitchItemLabel = signal(getTemplateSwitchItemLabel)

    private readonly $dragPlaceholder = viewChild.required("dragPlaceholder", {read: ViewContainerRef})

    ngOnInit() {
        this.$templateTree().registerChild(this)
    }

    ngOnDestroy() {
        this.$templateTree().unregisterChild(this)
    }

    onClickedNode(mouseEvent: MouseEvent) {
        const node = this.$node()
        const toggleMode = isApple ? mouseEvent.metaKey : mouseEvent.ctrlKey
        const selectedNodeParts = this.sceneManagerService.$selectedRootNodeParts()

        if (mouseEvent.shiftKey && selectedNodeParts.length > 0) {
            const nodeOwner = getNodeOwner(node)

            if (nodeOwner) {
                const previousSelection = selectedNodeParts[0]
                const {templateNode: previousSelectionNode} = previousSelection

                if (getNodeOwner(previousSelectionNode) === nodeOwner) {
                    const previousIndex = getOwnedNodes(nodeOwner).indexOf(previousSelectionNode)
                    const currentIndex = getOwnedNodes(nodeOwner).indexOf(node)

                    if (previousIndex !== -1 && currentIndex !== -1) {
                        const toAdd: ObjectPathPart[] = []
                        for (let i = previousIndex; ; currentIndex > previousIndex ? i++ : i--) {
                            toAdd.push({path: [getOwnedNodes(nodeOwner)[i]], part: "root"})
                            if (i === currentIndex) break
                        }
                        const base = toggleMode ? this.sceneManagerService.$selectedObjectPathParts() : []
                        this.sceneManagerService.selectNodes([...base, ...toAdd])
                        return
                    }
                }
            }
        }

        if (toggleMode) {
            const existing = selectedNodeParts.find((selectedNodePart) => selectedNodePart.templateNode === node)
            if (existing) this.sceneManagerService.removeNodeFromSelection({path: [existing.templateNode], part: existing.part})
            else this.sceneManagerService.addNodeToSelection({path: [node], part: "root"})
        } else {
            const templateNodePart = {templateNode: node, part: "root" as const}
            this.sceneManagerService.selectNode({path: [templateNodePart.templateNode], part: templateNodePart.part})
        }
    }

    onDoubleClickedNode(mouseEvent: MouseEvent) {
        const nodeOwner = this.$nodeOwnerNode()
        if (nodeOwner) this.selectChildren(mouseEvent)
        else this.renameNode()
    }

    onMouseEnter() {
        this.sceneManagerService.hoverNodes([{path: [this.$node()], part: "root"}])
    }

    onMouseLeave() {
        this.sceneManagerService.hoverNodes(undefined)
    }

    dragOver(event: DragEvent) {
        event.stopPropagation()

        const getDropPosition = (): DropPosition => {
            const element = this.elementRef.nativeElement
            const targetRect = element.getBoundingClientRect()

            const node = this.$treeNode()

            if (hasChildren(node)) {
                const offset = targetRect.height / 3
                const targetCenterY = targetRect.top + offset
                if (event.clientY < targetCenterY) return "before"
                else if (event.clientY < targetCenterY + offset) return "inside"
                else return "after"
            } else {
                const targetCenterY = targetRect.top + targetRect.height / 2
                return event.clientY < targetCenterY ? "before" : "after"
            }
        }

        const getDropTarget = (): TemplateDropTarget<TemplateTreeItemComponent> | null => {
            const dragSource = this.drag.$dragSource()
            if (!dragSource) return null

            const dropTarget = {component: this, position: this.forceDropPosition() ?? getDropPosition()}

            if (!this.isMovingToAllowed(dragSource, dropTarget.position)) return null
            return dropTarget
        }

        this.drag.$dropTarget.update((previous) => {
            const dropTarget = getDropTarget()
            if (previous === dropTarget) return previous
            if (
                previous &&
                dropTarget &&
                previous.component === dropTarget.component &&
                getDropPositionFromPosition(previous.position) === getDropPositionFromPosition(dropTarget.position)
            )
                return previous
            return dropTarget
        })

        if (this.drag.$dropTarget() !== null) event.preventDefault()
    }

    dragLeave(event: DragEvent) {
        event.stopPropagation()

        this.drag.dragLeave(event, this, this.elementRef.nativeElement)
    }

    isDropTarget(dropPosition: DropPosition) {
        if (!this.drag.$dragSource()) return false

        const dropTarget = this.drag.$dropTarget()
        if (!dropTarget) return false

        const {component} = dropTarget
        if (!(component instanceof TemplateTreeItemComponent)) return false

        const position = getDropPositionFromPosition(dropTarget.position)
        const dropTargetNode = component.$treeNode()

        const node = this.$treeNode()
        if (dropTargetNode === node) return position === dropPosition
        else if (position === "inside" && hasChildren(dropTargetNode)) {
            if (dropPosition === "after") {
                const children = getOwnedNodes(dropTargetNode.node)
                if (children.length > 0) {
                    const lastChild = children[children.length - 1]
                    return node.node === lastChild && node.parent === dropTargetNode
                }
            }
        }
        return false
    }

    private async allowLeavePage() {
        if (!this.sceneManagerService.$templateGraphModified()) return true

        const confirmed = await this.notifications.confirmationDialog({
            title: "Leave page ?",
            message: "Changes you made may not be saved. Are you sure you want to leave?",
            confirm: "Leave",
            cancel: "Stay",
        })

        return confirmed
    }

    async openTemplateReference(event: MouseEvent) {
        if (event.button !== 0 && event.button !== 1) return

        const templateReferenceNode = this.$templateReferenceNode()
        if (!templateReferenceNode) return

        const newTab = event.button === 1 || (event.ctrlKey && !isApple) || (event.metaKey && isApple)

        if (!newTab) {
            if (!(await this.allowLeavePage())) return
        }

        const {templateId, templateRevisionId} = templateReferenceNode.parameters
        const useAutoUpdate = templateRevisionId === null

        const templateData = await fetchThrowingErrors(this.templateGql)({legacyId: templateId})
        const template = templateData.template

        if (!this.$can().update.templateRevision({template})) {
            this.dialog.open(DialogComponent, {
                width: "400px",
                data: {
                    title: "Permission denied",
                    message: "You do not have permission to edit templates in other organisations.",
                    confirmLabel: "OK",
                },
            })
            return
        }

        const getTemplateIds = () => {
            if (useAutoUpdate) {
                const {latestRevision} = template
                if (!latestRevision) throw new Error("TemplateReference with invalid latest revision found, this should not happen.")
                return {templateId: template.id, templateRevisionId: latestRevision.id, type: template.type}
            } else {
                const revision = template.revisions.find((revision) => revision.legacyId === templateRevisionId)
                if (!revision) throw new Error("TemplateRevision not found")
                return {templateId: template.id, templateRevisionId: revision.id, type: template.type}
            }
        }

        const templateIds = getTemplateIds()

        const getType = () => {
            if (templateIds.type === TemplateType.Part || templateIds.type === TemplateType.Product) {
                return "products"
            } else if (templateIds.type === TemplateType.Scene) {
                return "scenes"
            } else {
                return "templates"
            }
        }

        const templateReferenceNodeLink = createLinkToEditorFromTemplatesForType(getType(), templateIds.templateId, templateIds.templateRevisionId)

        if (!newTab)
            this.router.navigate(templateReferenceNodeLink, {
                queryParamsHandling: "preserve",
            })
        else {
            const newWindow = window.open("", "_blank")
            if (newWindow) {
                newWindow.location.href = this.router.serializeUrl(this.router.createUrlTree(templateReferenceNodeLink, {queryParamsHandling: "preserve"}))
            } else {
                this.notifications.showError("Failed to open template reference in a new tab.")
            }
        }
    }

    async openMaterialReference(event: MouseEvent) {
        if (event.button !== 0 && event.button !== 1) return

        const materialReferenceNode = this.$materialReferenceNode()
        if (!materialReferenceNode) return

        const newTab = event.button === 1 || (event.ctrlKey && !isApple) || (event.metaKey && isApple)

        if (!newTab) {
            if (!(await this.allowLeavePage())) return
        }

        const {materialId, materialRevisionId} = materialReferenceNode.parameters
        const useAutoUpdate = materialRevisionId === null

        const getMaterialIds = async () => {
            const materialData = await fetchThrowingErrors(this.materialGql)({legacyId: materialId})

            if (useAutoUpdate) {
                const {latestCyclesRevision} = materialData.material
                if (!latestCyclesRevision) throw new Error("MaterialReference with invalid latest cycles revision found, this should not happen.")
                return {
                    materialId: materialData.material.id,
                    materialRevisionId: latestCyclesRevision.id,
                }
            } else {
                const revision = materialData.material.revisions.find((revision) => revision.legacyId === materialRevisionId)
                if (!revision) throw new Error("MaterialRevision not found")
                return {
                    materialId: materialData.material.id,
                    materialRevisionId: revision.id,
                }
            }
        }

        const materialIds = await getMaterialIds()

        const materialReferenceNodeLink = ["/materials", materialIds.materialId, "edit", materialIds.materialRevisionId]

        if (!newTab)
            void this.router.navigate(materialReferenceNodeLink, {
                queryParamsHandling: "preserve",
            })
        else {
            const newWindow = window.open("", "_blank")
            if (newWindow) {
                newWindow.location.href = this.router.serializeUrl(this.router.createUrlTree(materialReferenceNodeLink, {queryParamsHandling: "preserve"}))
            } else {
                this.notifications.showError("Failed to open material reference in a new tab.")
            }
        }
    }

    collapseAll() {
        const treeControl = this.$templateTree().treeControl
        const treeNode = this.$treeNode()
        treeControl.collapseDescendants(treeNode)
        treeControl.expand(treeNode)
    }

    renameNode() {
        const namedNode = this.$namedNode()
        if (!namedNode) return

        const dialogRef = this.dialog.open(RenameDialogComponent, {
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                currentName: namedNode.parameters.name,
            },
        })

        dialogRef
            .afterClosed()
            .pipe(
                tap((newValue) => {
                    if (newValue) {
                        this.sceneManagerService.modifyTemplateGraph(() => {
                            namedNode.updateParameters({name: newValue})
                        })
                    }
                }),
            )
            .subscribe()
    }

    private getProcessingNodes(includeRoot: boolean) {
        const selectedNodes = this.sceneManagerService.$selectedRootNodeParts().map((selectedNodePart) => selectedNodePart.templateNode)
        const node = this.$node()
        const copyNodes = selectedNodes.includes(node) ? selectedNodes : [node]
        return includeRoot ? copyNodes : copyNodes.filter((node) => node !== this.sceneManagerService.$templateGraph())
    }

    copySelection() {
        this.clipboardService.copy([...this.getProcessingNodes(true)])
    }

    pasteCopies() {
        const nodeOwner = this.$nodeOwnerNode()
        if (!nodeOwner || !this.clipboardService.$valid()) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            this.clipboardService.pasteDuplicates().forEach((node) => this.addReference(nodeOwner.parameters.nodes, node)),
        )
    }

    selectChildren(mouseEvent: MouseEvent) {
        const nodeOwner = this.$nodeOwnerNode()
        if (!nodeOwner) return

        const selectedNodeParts = this.sceneManagerService.$selectedRootNodeParts()
        const toggleMode = isApple ? mouseEvent.metaKey : mouseEvent.ctrlKey

        const toRemove: ObjectPathPart[] = []
        const toAdd: ObjectPathPart[] = []

        const doSelect = (node: TemplateNode) => {
            if (toggleMode) {
                const existing = selectedNodeParts.find((selectedNodePart) => selectedNodePart.templateNode === node)
                if (existing) toRemove.push({path: [existing.templateNode], part: existing.part})
                else toAdd.push({path: [node], part: "root"})
            } else toAdd.push({path: [node], part: "root"})

            if (isNodeOwnerLikeNode(node)) getOwnedNodes(node).forEach((childNode) => doSelect(childNode))
        }

        getOwnedNodes(nodeOwner).forEach((childNode) => doSelect(childNode))

        const initialSelection = mouseEvent.shiftKey ? this.sceneManagerService.$selectedObjectPathParts() : []
        const filtered = initialSelection.filter((p) => !toRemove.some((r) => objectPathPartEquals(p, r)))
        this.sceneManagerService.selectNodes([...filtered, ...toAdd])
    }

    private addReference<T extends TemplateNode>(nodes: TemplateListNode<T>, node: T, position?: {node: T; location: "before" | "after"}) {
        const {list} = nodes.parameters

        if (list.includes(node)) return
        if (!position) nodes.addEntry(node)
        else {
            const {location} = position
            const newList = [...list]
            if (location === "before") insertBefore(newList, position.node, node)
            else if (location === "after") insertAfter(newList, position.node, node)
            else throw new Error(`Invalid location: ${location}`)
            nodes.replaceParameters({list: newList})
        }
    }

    pasteReferences() {
        const switchNode = this.$switchNode()
        if (!switchNode || !this.clipboardService.$valid()) return

        const pastedNodes = this.clipboardService.pasteReferences().filter((node) => node !== switchNode)

        if (pastedNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            for (const fundamentalType of Object.values(fundamentalTypes) as AnyFundamentalType[]) {
                if (switchNode instanceof fundamentalType.Switch) {
                    const nodes = switchNode.parameters.nodes as TemplateListNode<TemplateNode>
                    pastedNodes.filter(fundamentalType.isLike).forEach((node) => this.addReference(nodes, node))
                    return
                }
            }
            throw new Error("Unsupported switch node type")
        })
    }

    duplicateNode() {
        const processingNodes = [...this.getProcessingNodes(false)]
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            const originalNodes = this.clipboardService.pasteReferences()

            this.clipboardService.copy(processingNodes)
            const copiedNodes = this.clipboardService.pasteDuplicates()

            processingNodes.forEach((node, index) => {
                const copiedNode = copiedNodes[index]

                if (node instanceof TemplateNode && copiedNode instanceof TemplateNode)
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, copiedNode, {node, location: "after"}))
            })

            this.clipboardService.copy(originalNodes)
        })
    }

    private getAllSubNodes = (node: TemplateNode, subNodes: Set<TemplateNode>) => {
        subNodes.add(node)
        if (isNodeOwnerLikeNode(node)) getOwnedNodes(node).forEach((childNode) => this.getAllSubNodes(childNode, subNodes))
    }

    private moveNodesToNewNodeOwner = (targetNode: NodeOwnerLikeNode, nodes: TemplateNode[], targetInsertLocation?: TemplateNode) => {
        const allSubNodes = new Set<TemplateNode>()
        nodes.forEach((node) => this.getAllSubNodes(node, allSubNodes))

        const firstUnselectedNodeOwner = (node: TemplateNode) => {
            const nodeOwner = getNodeOwner(node)
            if (!nodeOwner) throw new Error("Invalid node owner")

            if (allSubNodes.has(nodeOwner)) return firstUnselectedNodeOwner(nodeOwner)
            return {nodeOwner, node}
        }

        if (targetInsertLocation) {
            const {nodeOwner, node} = firstUnselectedNodeOwner(targetInsertLocation)
            this.addReference(nodeOwner.parameters.nodes, targetNode, {node, location: "after"})
        }

        nodes.forEach((node) => {
            if (node === targetNode) return

            const {nodeOwner, node: currentNode} = firstUnselectedNodeOwner(node)
            if (currentNode !== node) return

            if (nodeOwner !== targetNode) {
                nodeOwner.parameters.nodes.removeEntry(node)
                targetNode.parameters.nodes.addEntry(node)
            }
        })
    }

    createTemplate() {
        const processingNodes = [...this.getProcessingNodes(false)]
        if (processingNodes.length === 0) return

        const selectedNode = this.$node()

        const allSubNodes = new Set<TemplateNode>()
        processingNodes.forEach((node) => this.getAllSubNodes(node, allSubNodes))

        for (const node of allSubNodes) {
            if (node instanceof ConfigVariant) {
                const nodeOwner = getNodeOwner(node)
                if (!(nodeOwner instanceof ConfigGroup)) continue
                if (!allSubNodes.has(nodeOwner)) {
                    this.notificationService.showError("Cannot create a template from selection which includes a Config Variant but not its Config Group")
                    return
                }
            }
        }

        const allObjects = [...allSubNodes].filter(isInstanceOf(ObjectNode))

        const selectedObjects = processingNodes.filter(isInstanceOf(ObjectNode))

        const primaryObj = selectedObjects.at(0) ?? allObjects.at(0)

        const name = processingNodes.find(isNamedNode)?.parameters.name ?? "Template Graph"

        const dialogRef = this.dialog.open(RenameDialogComponent, {
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                currentName: name,
                caption: "Create Template From Selection",
            },
        })

        dialogRef
            .afterClosed()
            .pipe(
                tap((newValue) => {
                    if (newValue) {
                        this.sceneManagerService.modifyTemplateGraph(() => {
                            const matrixA = primaryObj ? this.sceneManagerService.getTransformAccessor(primaryObj)?.getTransform() : undefined

                            if (matrixA) {
                                for (const secondaryObject of allObjects) {
                                    const matrixB = this.sceneManagerService.getTransformAccessor(secondaryObject)?.getTransform()

                                    if (!matrixB) continue

                                    const newTransform = matrixA.inverse().multiply(matrixB).toArray()

                                    if (secondaryObject.parameters.lockedTransform) secondaryObject.updateParameters({lockedTransform: newTransform})
                                    else secondaryObject.updateParameters({$defaultTransform: newTransform})
                                }
                            }

                            const templateSubGraph = new TemplateGraph({
                                parameters: {
                                    name: newValue,
                                    nodes: new TemplateNodes({parameters: {list: []}}),
                                },
                            })

                            this.moveNodesToNewNodeOwner(templateSubGraph, processingNodes.filter(isInstanceOf(TemplateNode)), selectedNode)

                            if (matrixA) {
                                const instance = new TemplateInstance({
                                    parameters: {
                                        name: newValue + " instance",
                                        id: uuid4(),
                                        template: templateSubGraph,
                                        parameters: new Parameters({parameters: {}}),
                                        lockedTransform: matrixA.toArray(),
                                        visible: true,
                                    },
                                })

                                getNodeOwner(templateSubGraph, (nodeOwner) =>
                                    this.addReference(nodeOwner.parameters.nodes, instance, {node: templateSubGraph, location: "after"}),
                                )
                            }
                        })
                    }
                }),
            )
            .subscribe()
    }

    createInstance() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (isTemplateLikeNode(node)) {
                    const instance = new TemplateInstance({
                        parameters: {
                            name: isNamedNode(node) ? node.parameters.name + " instance" : "Template Instance",
                            id: uuid4(),
                            template: node,
                            parameters: new Parameters({parameters: {}}),
                            lockedTransform: Matrix4.identity().toArray(),
                            visible: true,
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => {
                        this.addReference(nodeOwner.parameters.nodes, instance, {node, location: "after"})
                    })
                }
            }),
        )
    }

    async promoteToLibraryTemplate() {
        const templateSubGraph = this.$templateGraphNode()
        if (!templateSubGraph) throw new Error("Invalid node type")
        const defaultCustomerId = this.sceneManagerService.$defaultCustomerId()

        const orginizationId = this.organizations.$all()?.find((organization) => organization.legacyId === defaultCustomerId)?.id
        if (!orginizationId) throw Error("Fitting organization found")

        const dialogRef = this.dialog.open<TemplateAddDialogComponent, TemplateAddDialogComponentData, boolean>(TemplateAddDialogComponent, {
            disableClose: false,
            data: {
                organizationId: orginizationId,
                name: templateSubGraph.parameters.name,
                state: TemplateState.Draft,
                templateType: TemplateType.Product,
            },
        })

        dialogRef.componentInstance.onTemplateCreated.subscribe((templateData) => {
            this.createTemplateRevision
                .mutate({input: {templateId: templateData.id, graph: templateSubGraph.serialize(), completed: false}})
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(({data}) => {
                    if (!data) return

                    this.sceneManagerService.modifyTemplateGraph((templateGraph) => {
                        getNodeOwner(templateSubGraph, (nodeOwner) => {
                            nodeOwner.parameters.nodes.removeEntry(templateSubGraph)
                        })
                        getAllTemplateNodes(templateGraph).forEach((node) => {
                            if (node instanceof TemplateInstance) {
                                if (node.parameters.template === templateSubGraph) {
                                    node.updateParameters({
                                        template: new TemplateReference({parameters: {templateId: templateData.legacyId, templateRevisionId: null}}),
                                    })
                                }
                            }
                        })
                    })

                    this.notificationService.showInfo(`New template ${templateData.name} saved, id: ${data.createTemplateRevision.id}`)
                })
        })
    }

    deleteReference() {
        const parentSwitchNode = this.$parentSwitchNode()
        if (!parentSwitchNode) return
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                ;(parentSwitchNode.parameters.nodes as TemplateListNode<TemplateNode>).removeEntry(node)
            }),
        )
    }

    deleteNode() {
        const processingNodes = this.getProcessingNodes(false)
        this.sceneManagerService.deleteNodes(processingNodes.map((node) => ({templateNode: node, part: "root"})))
    }

    moveSelectionToNewGroup() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        const selectedNode = this.$node()

        const name = processingNodes.find(isNamedNode)?.parameters.name ?? "Group"

        this.sceneManagerService.modifyTemplateGraph(() => {
            const group = new Group({parameters: {name, nodes: new TemplateNodes({parameters: {list: []}}), active: true}})
            this.moveNodesToNewNodeOwner(group, processingNodes.filter(isInstanceOf(TemplateNode)), selectedNode)
        })
    }

    dissolveGroup() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (isNodeOwnerLikeNode(node)) {
                    getNodeOwner(node, (nodeOwner) => {
                        const {list: childNodes} = node.parameters.nodes.parameters
                        const childNodesCopy = [...childNodes]
                        childNodesCopy.forEach((childNode) => {
                            node.parameters.nodes.removeEntry(childNode)
                            this.addReference(nodeOwner.parameters.nodes, childNode, {node, location: "before"})
                        })
                        nodeOwner.parameters.nodes.removeEntry(node)
                    })
                }
            }),
        )
    }

    addDecal() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (node instanceof MeshNode) {
                    const decal = new MeshDecal({
                        parameters: {
                            name: node.parameters.name + " decal",
                            mesh: node,
                            offset: [0, 0],
                            rotation: 0,
                            size: [10, 10],
                            distance: 0.01,
                            mask: undefined,
                            invertMask: false,
                            maskType: "binary",
                            materialAssignment: null,
                            visible: true,
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, decal, {node, location: "after"}))
                }
            }),
        )
    }

    addCurve() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return
        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (node instanceof MeshNode) {
                    const curve = new MeshCurve({
                        parameters: {
                            name: node.parameters.name + " curve",
                            mesh: node,
                            closed: false,
                            controlPoints: [],
                            visible: true,
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, curve, {node, location: "after"}))
                }
            }),
        )
    }

    readonly $blenderSendable = signal(false)

    // counted when the menu opens: collecting what a node holds walks the scene, too much to repeat for every node of the tree on each change
    updateBlenderSendable() {
        const {meshes, meshCurves, lights, cameras, renders, hdriLights, materialReferences} = this.forBlender()
        this.$blenderSendable.set([meshes, meshCurves, lights, cameras, renders, hdriLights, materialReferences].some((items) => items.length > 0))
    }

    private forBlender() {
        // a scene node's path holds objects and template instances only, so a group or config variant stands for what it holds
        const objectsBelow = (node: TemplateNode): TemplateNode[] => (isNodeOwnerLikeNode(node) ? getOwnedNodes(node).flatMap(objectsBelow) : [node])
        const sceneNodes = [
            ...new Set(this.getProcessingNodes(true).flatMap((node) => objectsBelow(node).flatMap((object) => this.sceneManagerService.getSceneNodes(object)))),
        ]
        return {
            meshes: sceneNodes.filter(SceneNodes.Mesh.is),
            meshCurves: sceneNodes.filter(SceneNodes.MeshCurveControl.is),
            lights: sceneNodes.filter(
                (node): node is BlenderLightSource =>
                    SceneNodes.AreaLight.is(node) || SceneNodes.PointLight.is(node) || SceneNodes.SunLight.is(node) || SceneNodes.LightPortal.is(node),
            ),
            cameras: sceneNodes.filter(SceneNodes.Camera.is),
            renders: sceneNodes.filter(SceneNodes.RenderSettings.is),
            hdriLights: [...new Set(this.getProcessingNodes(true).flatMap(objectsBelow).filter(isInstanceOf(HdriLight)))],
            materialReferences: this.getProcessingNodes(false)
                .filter(isInstanceOf(MaterialReference))
                .map(({parameters: {materialId, materialRevisionId}}) => ({materialId, materialRevisionId})),
        }
    }

    async sendToBlender() {
        const blenderConnectorService = this.blenderConnectorService
        if (!blenderConnectorService) return
        const {meshes, meshCurves, lights, cameras, renders, hdriLights, materialReferences} = this.forBlender()
        const data: SendToBlenderDialogData = {
            meshCount: meshes.length,
            meshCurveCount: meshCurves.length,
            lightCount: lights.length,
            cameraCount: cameras.length,
            materialCount: materialReferences.length,
            hdriCount: hdriLights.length,
            renderCount: renders.length,
        }
        const result = await firstValueFrom(
            this.dialog
                .open<SendToBlenderDialogComponent, SendToBlenderDialogData, SendToBlenderDialogResult>(SendToBlenderDialogComponent, {data})
                .afterClosed(),
        )
        if (result === undefined) return
        const count = [
            countOf(meshes.length, "mesh", "meshes"),
            countOf(meshCurves.length, "curve", "curves"),
            countOf(lights.length, "light", "lights"),
            countOf(cameras.length, "camera", "cameras"),
            countOf(materialReferences.length, "material", "materials"),
            countOf(hdriLights.length, "HDRI", "HDRIs"),
            renders.length > 0 ? "render settings" : "",
        ]
            .filter((part) => part !== "")
            .join(", ")
        this.notificationService.showInfo(`Preparing ${count} for Blender…`, 0)
        try {
            await blenderConnectorService.sendToBlender({
                meshes: {nodes: meshes, includeMaterials: result.includeMaterials},
                meshCurves,
                lights,
                cameras,
                materialReferences,
                hdriLights,
                render: renders[0] ?? null,
            })
            this.notificationService.showInfo(`Sent ${count} to Blender`)
        } catch (error) {
            this.notificationService.showError(error)
        }
    }

    addDimensionGuides() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (node instanceof ObjectNode) {
                    const name = isNamedNode(node) ? node.parameters.name + " dimension guide" : "Dimension guide"
                    const dimensionGuide = initDimensionGuides(name, node)
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, dimensionGuide, {node, location: "after"}))
                }
            }),
        )
    }

    addDimensionGuideBox() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (node instanceof ObjectNode) {
                    const meshes = this.sceneManagerService.getSceneNodes(node).filter(SceneNodes.Mesh.is)

                    let [width, height, depth] = [100, 100, 100]
                    let lockedTransform = Matrix4.identity().toArray()
                    if (meshes.length === 1) {
                        const mesh = meshes[0]
                        const {aabb, centroid} = mesh.completeMeshData.reified.bounds
                        ;[width, height, depth] = [aabb[1][0] - aabb[0][0], aabb[1][1] - aabb[0][1], aabb[1][2] - aabb[0][2]]
                        lockedTransform = Matrix4.translation(...centroid).toArray()
                    }
                    const name = isNamedNode(node) ? node.parameters.name + " dimension guide box " : "Dimension guide box"
                    const dimensionGuide = new DimensionGuideBox({
                        parameters: {
                            name,
                            parent: node,
                            visible: true,
                            width,
                            height,
                            depth,
                            lockedTransform,
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, dimensionGuide, {node, location: "after"}))
                }
            }),
        )
    }

    addSeam() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (node instanceof MeshCurve) {
                    const curve = new Seam({
                        parameters: {
                            name: node.parameters.name + " seam",
                            curves: [node],
                            item: null,
                            allowScaling: false,
                            visible: true,
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, curve, {node, location: "after"}))
                }
            }),
        )
    }

    addMoveMaterial() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (isMaterialLikeNode(node)) {
                    const curve = new MoveMaterial({
                        parameters: {
                            name: `Moved ${isNamedNode(node) ? node.parameters.name : getTemplateNodeLabel(node)}`,
                            material: node,
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, curve, {node, location: "after"}))
                }
            }),
        )
    }

    addImageOperator() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (isImageLikeNode(node)) {
                    const operator = new ImageOperator({
                        parameters: {
                            name: isNamedNode(node) ? node.parameters.name + " operator" : "Image Operator",
                            input: node,
                            operation: "ADD",
                            value: 0,
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, operator, {node, location: "after"}))
                }
            }),
        )
    }

    addImageRgbCurve() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (isImageLikeNode(node)) {
                    const operator = new ImageRgbCurve({
                        parameters: {
                            name: isNamedNode(node) ? node.parameters.name + " rgb curve" : "Image RGB Curve",
                            input: node,
                            r: [
                                {x: 0, y: 0},
                                {x: 1, y: 1},
                            ],
                            g: [
                                {x: 0, y: 0},
                                {x: 1, y: 1},
                            ],
                            b: [
                                {x: 0, y: 0},
                                {x: 1, y: 1},
                            ],
                            rgb: [
                                {x: 0, y: 0},
                                {x: 1, y: 1},
                            ],
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, operator, {node, location: "after"}))
                }
            }),
        )
    }

    addMoveImage() {
        const processingNodes = this.getProcessingNodes(false)
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() =>
            processingNodes.forEach((node) => {
                if (isImageLikeNode(node)) {
                    const moveImage = new MoveImage({
                        parameters: {
                            name: `Moved ${isNamedNode(node) ? node.parameters.name : node.nodeClass}`,
                            image: node,
                        },
                    })
                    getNodeOwner(node, (nodeOwner) => this.addReference(nodeOwner.parameters.nodes, moveImage, {node, location: "after"}))
                }
            }),
        )
    }

    forceDropPosition(): DropPosition | undefined {
        if (this.$isRootNode()) return "inside"
        return undefined
    }

    private targetSwitchNode(position: DropPosition) {
        if (position === "inside") return this.$switchNode()
        return this.$parentSwitchNode()
    }

    isMovingToAllowed(source: DraggableSource, position: DropPosition) {
        const isMovingToAllowedSingle = (source: TemplateNode | TemplateTreeItemComponent) => {
            const sourceNode = this.drag.draggableSourceToTemplateNode(source)
            if (!sourceNode) return false

            if (source instanceof TemplateTreeItemComponent) {
                // Prevent dropping on itself
                if (source === this) return false
            }

            const targetSwitchNode = this.targetSwitchNode(position)

            //Prevent mixing references and non-references
            const sourceIsReference =
                source instanceof TemplateTreeItemComponent
                    ? source.$parentSwitchNode() !== undefined
                    : sourceNode instanceof TemplateNode && getNodeOwner(sourceNode) !== null
            const targetIsReference = targetSwitchNode !== undefined
            if (sourceIsReference !== targetIsReference) return false

            //Prevent dropping a node into a parent it is already part of
            if (source instanceof TemplateTreeItemComponent) if (position === "inside" && source.$parent() === this.$treeNode()) return false

            //Prevent dropping a node into a switch node that does not support it
            if (targetSwitchNode) {
                const fundamentalType = (Object.values(fundamentalTypes) as AnyFundamentalType[]).find((ft) => targetSwitchNode instanceof ft.Switch)
                if (!fundamentalType) throw new Error("Unsupported switch node type")
                if (!fundamentalType.isLike(sourceNode)) return false
            } else if (!(sourceNode instanceof TemplateNode)) return false

            //Prevent dropping a node into one of its descendants
            if (source instanceof TemplateTreeItemComponent) {
                const sourceTreeNode = source.$treeNode()
                let {parent} = this.$treeNode()
                while (parent) {
                    if (parent === sourceTreeNode) return false
                    parent = parent.parent
                }
            }

            return true
        }

        if (source instanceof Array) {
            return source.every((source) => isMovingToAllowedSingle(source))
        } else return isMovingToAllowedSingle(source)
    }

    moveNodeTo(source: DraggableSource, position: DropPosition) {
        if (!this.isMovingToAllowed(source, position)) return

        const targetNode = this.$node()
        const targetSwitchNode = this.targetSwitchNode(position)

        const moveNodeToSingle = (source: TemplateNode | TemplateTreeItemComponent) => {
            const sourceNode = this.drag.draggableSourceToTemplateNode(source)
            if (!sourceNode) return

            if (targetSwitchNode) {
                const sourceSwitchParent = source instanceof TemplateTreeItemComponent ? source.$parentSwitchNode() : undefined
                if (sourceSwitchParent) {
                    ;(sourceSwitchParent.parameters.nodes as TemplateListNode<TemplateNode>).removeEntry(sourceNode)
                }

                const getPosition = <T extends TemplateNode>(nodes: TemplateListNode<T>) => {
                    if (position === "inside") return undefined
                    const item = nodes.entries.find((x) => x === targetNode)
                    if (!item) return undefined
                    return {node: item, location: position}
                }

                const fundamentalType = (Object.values(fundamentalTypes) as AnyFundamentalType[]).find((ft) => targetSwitchNode instanceof ft.Switch)
                if (!fundamentalType) throw new Error("Unsupported switch node type")
                if (!fundamentalType.isLike(sourceNode)) throw new Error("Invalid node type")
                const nodes = targetSwitchNode.parameters.nodes as TemplateListNode<TemplateNode>
                this.addReference(nodes, sourceNode, getPosition(nodes))
            } else {
                if (!(sourceNode instanceof TemplateNode)) throw new Error("Source node is not of type node")

                const sourceNodeOwner = getNodeOwner(sourceNode)
                if (sourceNodeOwner) sourceNodeOwner.parameters.nodes.removeEntry(sourceNode)

                if (position === "inside") {
                    if (!isNodeOwnerLikeNode(targetNode)) throw new Error("Target node is not of type node parent")
                    this.addReference(targetNode.parameters.nodes, sourceNode)
                } else {
                    if (!(targetNode instanceof TemplateNode)) throw new Error("Target node is not of type node")
                    const targetNodeOwner = getNodeOwner(targetNode)
                    if (!targetNodeOwner) throw new Error("Target node is not of type node parent")
                    this.addReference(targetNodeOwner.parameters.nodes, sourceNode, {node: targetNode, location: position})
                }
            }
        }

        this.sceneManagerService.modifyTemplateGraph(() => {
            if (source instanceof Array) source.forEach((source) => moveNodeToSingle(source))
            else moveNodeToSingle(source)
        })
    }

    gotoReference() {
        if (!this.$parentSwitchNode()) return
        this.sceneManagerService.selectNode({path: [this.$node()], part: "root"})
    }

    setVisibility(visible: boolean) {
        const processingNodes = this.$objectProcessingNodes()
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            processingNodes.forEach((templateNode) => {
                templateNode.updateParameters({
                    visible,
                })
            })
        })
    }

    private getDragImage() {
        const dragPlaceholder = this.$dragPlaceholder()

        const dragImage = document.createElement("div")

        dragImage.style.display = "flex"
        dragImage.style.flexDirection = "column"
        dragImage.style.justifyContent = "center"
        dragImage.style.alignItems = "left"
        dragImage.style.position = "absolute"
        dragImage.style.zIndex = "1000"
        dragImage.style.left = "-1000px"
        dragImage.style.backgroundColor = "rgb(255, 255, 255)"
        dragImage.style.border = "1px solid black"
        dragImage.style.padding = "5px"
        dragImage.style.fontSize = "0.75rem"
        dragImage.style.fontWeight = "500"
        dragImage.style.color = "black"
        dragImage.style.gap = "5px"

        const draggedComponents = this.getDraggedComponents()

        for (const draggedComponent of draggedComponents) {
            const templateNodeComponent = dragPlaceholder.createComponent(TemplateNodeComponent)
            templateNodeComponent.setInput("node", draggedComponent.$node())
            dragImage.appendChild(templateNodeComponent.location.nativeElement)
        }

        this.cdr.detectChanges()

        const nativeElement = dragPlaceholder.element.nativeElement as HTMLElement

        nativeElement.appendChild(dragImage)

        const dpr = window.devicePixelRatio || 1
        const rect = dragImage.getBoundingClientRect()
        dragImage.style.width = `${Math.ceil(rect.width * dpr) / dpr}px`
        dragImage.style.height = `${Math.ceil(rect.height * dpr) / dpr}px`

        setTimeout(() => {
            nativeElement.removeChild(dragImage)
        }, 0)

        return dragImage
    }

    private getDraggedComponents() {
        const processingNodes = this.getProcessingNodes(false)

        const templateTreeItems = this.$templateTree().$children()

        const draggedParentSwitchNode = this.$parentSwitchNode()

        return processingNodes
            .map((node) => {
                return templateTreeItems.find((child) => {
                    if (child.$node() !== node) return false
                    if (child.$parentSwitchNode() !== draggedParentSwitchNode) return false
                    return true
                })
            })
            .filter((child): child is TemplateTreeItemComponent => child !== undefined)
    }

    dragStart(event: DragEvent) {
        const dragImage = this.getDragImage()

        event.dataTransfer?.setDragImage(dragImage, 0, 0)

        this.drag.dragStart(event, this.getDraggedComponents())
    }
}
